import { type FC, useEffect, useState } from 'react'
import { FormProvider, useForm, type UseFormProps } from 'react-hook-form'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import { Button, Input } from 'base-ui'

import {
  BaseHookFormErrorMessage,
  RadioInputGroupHookFormController,
  type RadioParams,
  useRunResetOnSubmitSuccessfull,
  validatePhoneNumber,
} from '../../../base-form-handling'
import {
  FriendlyCaptchaChallenge,
  useHookFormFriendlyCaptchaFieldControls,
} from '../../../friendly-captcha'
import { useApiSwrMediServiceContactRxCallbackMutation } from '../../api/useApiSwrMediServiceContactRxCallbackMutation'
import { type MediServicePhoneServiceFormFields } from '../../model/MediServicePhoneServiceFormFields'
import { rxGetCampaignCode } from '../../rxCampaignCode'
import {
  type FormSubmissionStatus,
  MediServiceFormSubmissionNotification,
} from '../MediServiceFormSubmissionNotification/MediServiceFormSubmissionNotification'

const onSubmit = async (
  data: MediServicePhoneServiceFormFields,
  submitMutation: ReturnType<
    typeof useApiSwrMediServiceContactRxCallbackMutation
  >,
  setFormSubmissionStatus: (status: FormSubmissionStatus) => void,
): Promise<void> => {
  if (submitMutation.isMutating) {
    return
  }

  try {
    await submitMutation.trigger(data)
    setFormSubmissionStatus('success')
  } catch {
    setFormSubmissionStatus('error')
  }
}

const getTimeslotParams = (intl: IntlShape): RadioParams[] => [
  {
    dataQaId: 'chrx.further-info.phone-service.schedule.asap',
    id: 'phone-asap',
    label: intl.formatMessage({
      id: 'chrx.further-info.phone-service.schedule.asap',
    }),
    value: 'chrx.further-info.phone-service.schedule.asap',
  },
  {
    dataQaId: 'chrx.further-info.phone-service.schedule.08-00-to-11-30',
    id: 'phone-1',
    label: intl.formatMessage({
      id: 'chrx.further-info.phone-service.schedule.08-00-to-11-30',
    }),
    value: 'chrx.further-info.phone-service.schedule.08-00-to-11-30',
  },
  {
    dataQaId: 'chrx.further-info.phone-service.schedule.11-30-to-13-30',
    id: 'phone-2',
    label: intl.formatMessage({
      id: 'chrx.further-info.phone-service.schedule.11-30-to-13-30',
    }),
    value: 'chrx.further-info.phone-service.schedule.11-30-to-13-30',
  },
  {
    dataQaId: 'chrx.further-info.phone-service.schedule.13-30-to-16-30',
    id: 'phone-3',
    label: intl.formatMessage({
      id: 'chrx.further-info.phone-service.schedule.13-30-to-16-30',
    }),
    value: 'chrx.further-info.phone-service.schedule.13-30-to-16-30',
  },
  {
    dataQaId: 'chrx.further-info.phone-service.schedule.16-30-to-18-00',
    id: 'phone-4',
    label: intl.formatMessage({
      id: 'chrx.further-info.phone-service.schedule.16-30-to-18-00',
    }),
    value: 'chrx.further-info.phone-service.schedule.16-30-to-18-00',
  },
]

const formParams: UseFormProps<MediServicePhoneServiceFormFields> = {
  defaultValues: {
    option: 'chrx.further-info.phone-service.schedule.asap',
  },
  mode: 'onBlur',
}

export const MediServicePhoneServiceForm: FC = () => {
  const formSubmitMutation = useApiSwrMediServiceContactRxCallbackMutation()
  const [formSubmissionStatus, setFormSubmissionStatus] =
    useState<FormSubmissionStatus>(false)
  const intl = useIntl()
  const formMethods = useForm<MediServicePhoneServiceFormFields>(formParams)
  const { control, formState, handleSubmit, register, reset, setValue, watch } =
    formMethods

  useEffect(() => {
    const campaignCodePhone = rxGetCampaignCode()
    if (campaignCodePhone) {
      setValue('campaignCode', campaignCodePhone)
    }
  }, [setValue])
  const timeslotParams = getTimeslotParams(intl)

  useRunResetOnSubmitSuccessfull(formState.isSubmitSuccessful, reset)
  const { onCaptchaDone, watchFriendlyCaptchaChange } =
    useHookFormFriendlyCaptchaFieldControls<MediServicePhoneServiceFormFields>({
      fieldName: 'fcSolution',
      register,
      setValue,
      watch,
    })

  /* eslint-disable @typescript-eslint/no-misused-promises -- the rule was disabled before a comment became required */
  return (
    <FormProvider {...formMethods}>
      <form
        id="phone-service-form"
        noValidate
        onSubmit={handleSubmit(async (data) => {
          await onSubmit(data, formSubmitMutation, setFormSubmissionStatus)
        })}
      >
        <div className="flex flex-col items-start gap-4">
          <div className="flex flex-1 flex-col items-start gap-2.5 self-stretch">
            <Input
              {...register('phone', {
                required: intl.formatMessage({
                  id: 'chrx.messages.validation.required',
                }),
                validate: (input) => {
                  const { valid } = validatePhoneNumber(input)

                  return (
                    valid ||
                    intl.formatMessage({
                      id: 'chrx.messages.validation.phone',
                    })
                  )
                },
              })}
              aria-label={intl.formatMessage({
                id: 'chrx.further-info.phone-service.phone-number-placeholder',
              })}
              className="flex-1 self-stretch"
              data-qa-id="chrx.further-info.phone-service.phone-number-placeholder"
              id="phone"
              name="phone"
              placeholder={`${intl.formatMessage({
                id: 'chrx.further-info.phone-service.phone-number-placeholder',
              })}*`}
            />
            <BaseHookFormErrorMessage
              data-qa-id="chrx-phone-form-telephone-error"
              name="phone"
            />
          </div>
          <RadioInputGroupHookFormController
            className="flex flex-col items-start gap-3 self-stretch p-0"
            /* @ts-expect-error need more generic type here */
            control={control}
            legendMessage={intl.formatMessage({
              id: 'chrx.further-info.phone-service.timeRange.legend',
            })}
            name="option"
            radios={timeslotParams}
          />
          <FriendlyCaptchaChallenge
            className="mb-3.5"
            doneCallback={onCaptchaDone}
            startMode="auto"
            whenThisPropChangesResetCaptcha={
              formSubmitMutation.data ?? formSubmitMutation.error
            }
          />
          <Button
            className="min-h-[48px] self-stretch"
            data-qa-id="chrx.further-info.phone-service.submit-label"
            disabled={!watchFriendlyCaptchaChange}
            type="submit"
            variant="outlined"
          >
            <FormattedMessage id="chrx.further-info.phone-service.submit-label" />
          </Button>
          <MediServiceFormSubmissionNotification
            dataQaId={`chrx-phone-form-submission-${formSubmissionStatus}`}
            formSubmissionStatus={formSubmissionStatus}
          />
        </div>
      </form>
    </FormProvider>
  )
  /* eslint-enable @typescript-eslint/no-misused-promises */
}
